import type { CSSRuleObject } from "tailwindcss/types/config";
import { ColorSchema } from "./colorToken";

type ConfigValues = Record<number | string, string> | string;

export enum ColorToken {
  BLUE = "rmh-blue",
  DEEP_BLUE = "rmh-deepblue",
  TEAL = "rmh-teal",
  GREEN = "rmh-green",
  RED = "rmh-red",
  TAN = "rmh-tan",
  ORANGE = "rmh-orange",
  YELLOW = "rmh-yellow",
  BLUE_GRAY = "rmh-bluegray",
  GRAY = "rmh-gray",
  WHITE = "rmh-white",
  SLATE = "rmh-slate",
  SAPPHIRE_BLUE = "rmh-sapphire-blue",
  ACTIVE_BTN = "rmh-active-button",
}

export enum ShadowToken {
  ACTIVE_INPUT = "active-input",
  MENU_SHADOW = "menu-shadow",
}

export const CUSTOM_COLORS: Record<ColorToken, ConfigValues> = {
  [ColorToken.BLUE]: {
    5: "#F5F5F5",
    25: "#EFF3FC",
    50: "#F3FBFF",
    100: "#D4EDFB",
    200: "#A3D6F2",
    300: "#62BCEE",
    400: "#22A4EC",
    500: "#0081C8", // Tertiary Color
    600: "#046DA7",
    700: "#025583",
    800: "#013F62",
    900: "#002E48",
  },
  [ColorToken.DEEP_BLUE]: {
    25: "#EFF3FC",
    50: "#D0DAF4",
    100: "#A4B8EC",
    200: "#6F8EE1",
    300: "#4165BB",
    400: "#31519E",
    500: "#2B478B", // Primary Color
    600: "#213974",
    700: "#1B3063",
    800: "#14254D",
    900: "#0B162F",
  },
  [ColorToken.TEAL]: {
    100: "#F4FBF9",
    200: "#E6F5F1",
    300: "#D2EEE5",
    400: "#B9E3D5",
    500: "#A4D3C3",
    600: "#92CAB7",
    700: "#7CB9A4",
    800: "#5FA78E",
    900: "#3D7A64",
  },
  [ColorToken.SAPPHIRE_BLUE]: {
    20: "#F3F9FF",
  },
  [ColorToken.GREEN]: {
    50: "#F6FFF0",
    100: "#DBFAC8",
    200: "#B7F292",
    300: "#99F362",
    400: "#7EED3A",
    500: "#64E018", // Secondary Color
    600: "#55C013",
    700: "#449D0D", // Success Color
    800: "#367E09",
    900: "#265807",
  },
  [ColorToken.RED]: {
    50: "#FFF6F6",
    100: "#FFE1E1",
    200: "#F9B5B5",
    300: "#F66F6F",
    400: "#F43C3C",
    500: "#F40909", // Error Color
    600: "#D30B0B",
    700: "#AF0E0E",
    800: "#820808",
    900: "#600606",
  },
  [ColorToken.TAN]: {
    100: "#FCFBF7",
    200: "#F6F5EE",
    300: "#F3F1E3",
    400: "#EDEAD5",
    500: "#E3DFC3",
    600: "#D4D0AF",
    700: "#C1BD9C",
    800: "#AEAA88",
    900: "#858160",
  },
  [ColorToken.ORANGE]: {
    50: "#FFFCF5",
    100: "#FFF4DE",
    200: "#FBDCA1",
    300: "#FBC967",
    400: "#F5B73C",
    500: "#F4A409", // Warning Color
    600: "#DD9509",
    700: "#B57A08",
    800: "#8B5E05",
    900: "#5F4005",
  },
  [ColorToken.YELLOW]: {
    50: "#FAFAF1",
    100: "#F6F6E4",
    200: "#F6F6C6",
    300: "#F3F29C",
    400: "#F2F074",
    500: "#E7E454",
    600: "#C5C240",
    700: "#AEAC34",
    800: "#8E8C29",
    900: "#615F17",
  },
  [ColorToken.BLUE_GRAY]: {
    25: "#FCFDFF",
    100: "#F6FAFB",
    200: "#EBF4F6",
    300: "#DFEDF2",
    400: "#CEE2E9",
    500: "#BAD4DC",
    600: "#A5BDC5",
    700: "#869DA5",
    800: "#5C7077",
    900: "#38484E",
  },
  [ColorToken.GRAY]: {
    0: "#FFFFFF",
    50: "#F6F7FA", // Background Color
    100: "#EDEFF2",
    200: "#D9DBDF",
    300: "#C3C6CC", // Stroke Color
    400: "#AFB3BB",
    500: "#9296A0",
    600: "#6B6F7B", // Secondary Text Color
    700: "#4F525E",
    800: "#343741", // Primary Text Color
    900: "#26282C",
  },
  [ColorToken.SLATE]: {
    50: "#F7F8F9",
    100: "#E9EBED",
    200: "#D2D5DA",
    300: "#B7BCC4",
    400: "#9EA4AD",
    500: "#7E8690",
    600: "#666E7A",
    700: "#4D545E",
    800: "#232D45",
    900: "#2B2E36",
  },
  [ColorToken.WHITE]: "#FFF",
  [ColorToken.ACTIVE_BTN]: "hsla(222, 49%, 49%, 1)",
  ...ColorSchema,
};

export const CUSTOM_SHADOW: Record<ShadowToken, string> = {
  [ShadowToken.ACTIVE_INPUT]: `0px 0px 0px 2px ${
    CUSTOM_COLORS[ColorToken.BLUE][400]
  }`,
  [ShadowToken.MENU_SHADOW]: `0px 2px 8px 0px ${
    CUSTOM_COLORS[ColorToken.GRAY][500]
  }`,
};

export const CUSTOM_COMPONENTS: CSSRuleObject = {
  ".display": {
    fontSize: "36px",
    lineHeight: "40px",
    fontWeight: "500",
  },
  ".body-l": {
    fontSize: "16px",
    lineHeight: "20px",
    fontWeight: "400",
  },
  ".body-m": {
    fontSize: "14px",
    lineHeight: "16px",
    fontWeight: "400",
  },
  ".body-l-strong": {
    fontWeight: "600",
    fontSize: "16px",
    lineHeight: "20px",
  },
  ".body-m-strong": {
    fontWeight: "600",
    fontSize: "14px",
    lineHeight: "16px",
  },
  ".overline-l": {
    fontWeight: "500",
    fontSize: "14px",
    lineHeight: "16px",
  },
  ".overline-m": {
    fontWeight: "500",
    fontSize: "12px",
    lineHeight: "14px",
  },
  ".header": {
    fontWeight: "700",
    fontSize: "24px",
    lineHeight: "28px",
  },
  ".title-l": {
    fontWeight: "600",
    fontSize: "22px",
    lineHeight: "26px",
  },
  ".title-m": {
    fontWeight: "600",
    fontSize: "20px",
    lineHeight: "24px",
  },
  ".title-s": {
    fontWeight: "600",
    fontSize: "18px",
    lineHeight: "22px",
  },
  ".subtitle-s": {
    fontWeight: "400",
    fontSize: "16px",
    lineHeight: "20px",
  },
  ".subtitle-m": {
    fontWeight: "400",
    fontSize: "18px",
    lineHeight: "22px",
  },
  ".subtitle-l": {
    fontWeight: "400",
    fontSize: "20px",
    lineHeight: "24px",
  },
  ".caption-l": {
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "16px",
  },
  ".button-s": {
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: "14px",
  },
  ".button-m": {
    fontSize: "14px",
    fontWeight: "600",
    lineHeight: "16px",
  },
  ".button-l": {
    fontSize: "16px",
    fontWeight: "600",
    lineHeight: "18px",
  },
  ".headline": {
    fontSize: "32px",
    fontWeight: "600",
    lineHeight: "36px",
  },
};
